import { getCurrentLocale } from "./locale";

/**
 * Date helpers for the Activity Monitoring / Activity Trend charts.
 *
 * The backend serves per-day activity buckets keyed by a calendar-date string
 * (`YYYY-MM-DD`, e.g. "2026-07-16"). These are already the viewer's local day
 * (the backend buckets by the `X-Timezone` we send), so they must be rendered
 * exactly as given. Rendering them with a bare `new Date("2026-07-16")` parses
 * them as UTC midnight and then formats in the browser's LOCAL timezone, which
 * shifts the label by a day for viewers behind UTC (e.g. "Jul 16" -> "Jul 15").
 *
 * These helpers format calendar-date strings in UTC so the label matches the
 * `YYYY-MM-DD` value verbatim (no day shift). Full instant timestamps (with a
 * time component / offset, e.g. a fall's `peakAt`) keep the viewer's local
 * timezone.
 */

/** True for bare `YYYY-MM-DD` calendar-date strings (no time component). */
export const isCalendarDate = (value) =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

/**
 * Format a short day label like "Jul 16".
 * Calendar dates are rendered in UTC; instants in the viewer's local timezone.
 *
 * @param {string} value - `YYYY-MM-DD` calendar date or a full ISO instant.
 * @param {{ withYear?: boolean }} [options]
 * @returns {string} e.g. "Jul 16" or "Jul 16, 2026"; "" when input is empty/invalid.
 */
export const formatDayLabel = (value, options) => {
  if (!value) return "";
  const withYear = !!(options && options.withYear);
  const calendar = isCalendarDate(value);
  const date = new Date(calendar ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(date.getTime())) return "";

  const fmt = { month: "short", day: "numeric" };
  if (withYear) fmt.year = "numeric";
  if (calendar) fmt.timeZone = "UTC";
  return date.toLocaleDateString(
    getCurrentLocale() === "ja" ? "ja-JP" : "en-US",
    fmt,
  );
};
